import React from "react";
import {
  Box,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  TextField,
  Typography,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  BusinessTemplateType,
  CustomSettings,
} from "subModule/src/services/invoice/types";

interface LogoSettingsPanelProps {
  settings: CustomSettings;
  onChange: (settings: Partial<CustomSettings>) => void;
  disabled?: boolean;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5, DELIVERY_NOTE = 6
  /** Thermal invoice: QR placement is driven by Layout order (`qr` section), not qrPosition */
  isThermalInvoice?: boolean;
}

/** Matches invoice preview content max width (~A4 printable area at 96dpi) */
const PAGE_CONTENT_MAX_WIDTH_PX = 785;

const LogoSettingsPanel: React.FC<LogoSettingsPanelProps> = ({
  settings,
  onChange,
  disabled = false,
  templateType = 2,
  isThermalInvoice = false,
}) => {
  const { t } = useTranslation();

  const positionOptions = [
    { value: "left", label: t("left") },
    { value: "center", label: t("center") },
    { value: "right", label: t("right") },
    { value: "none", label: t("hide") || "Hide" },
  ];

  const fontWeightOptions = [
    { value: 300, label: "Light (300)" },
    { value: 400, label: "Normal (400)" },
    { value: 500, label: "Medium (500)" },
    { value: 600, label: "Semi Bold (600)" },
    { value: 700, label: "Bold (700)" },
    { value: 800, label: "Extra Bold (800)" },
    { value: 900, label: "Black (900)" },
  ];

  // Helper functions to get field names based on type
  const getShowNumberField = () => {
    if (templateType === 3) return settings.showJobcardNumber;
    if (templateType === 4) return settings.showWorkorderNumber;
    if (templateType === 6) return settings.showDeliveryNoteNumber;
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.showProformaInvoiceNumber;
    }
    if (templateType === 5) return settings.showInvoiceNumber; // For inspection, show template heading
    return settings.showInvoiceNumber;
  };

  const getNumberPositionField = () => {
    if (templateType === 3) return settings.jobcardNumberPosition;
    if (templateType === 4) return settings.workorderNumberPosition;
    if (templateType === 6) return settings.deliveryNoteNumberPosition;
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.proformaInvoiceNumberPosition;
    }
    return settings.invoiceNumberPosition;
  };

  const getNumberFontSizeField = () => {
    if (templateType === 3) return settings.jobcardNumberFontSize;
    if (templateType === 4) return settings.workorderNumberFontSize;
    if (templateType === 6) return settings.deliveryNoteNumberFontSize;
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.proformaInvoiceNumberFontSize;
    }
    return settings.invoiceNumberFontSize;
  };

  const getNumberFontWeightField = () => {
    if (templateType === 3) return settings.jobcardNumberFontWeight;
    if (templateType === 4) return settings.workorderNumberFontWeight;
    if (templateType === 6) return settings.deliveryNoteNumberFontWeight;
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return settings.proformaInvoiceNumberFontWeight;
    }
    return settings.invoiceNumberFontWeight;
  };

  const getLabelText = () => {
    if (templateType === 3)
      return t("showJobcardNumber") || "Show Jobcard Number";
    if (templateType === 4)
      return t("showWorkorderNumber") || "Show Workorder Number";
    if (templateType === 6)
      return t("showDeliveryNoteNumber") || "Show Delivery Note Number";
    if (templateType === 5)
      return t("showTemplateHeading") || "Show Template Heading";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return t("showProformaInvoiceNumber") || "Show Proforma Invoice Number";
    }
    return t("showInvoiceNumber") || "Show Invoice Number";
  };

  const getPositionLabel = () => {
    if (templateType === 3)
      return t("jobcardNumberPosition") || "Jobcard Number Position";
    if (templateType === 4)
      return t("workorderNumberPosition") || "Workorder Number Position";
    if (templateType === 6)
      return t("deliveryNoteNumberPosition") || "Delivery Note Number Position";
    if (templateType === 5)
      return t("inspectionNumberPosition") || "Inspection Number Position";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return (
        t("proformaInvoiceNumberPosition") || "Proforma Invoice Number Position"
      );
    }
    return t("invoiceNumberPosition") || "Invoice Number Position";
  };

  const getDirectionLabel = () => {
    if (templateType === 3)
      return t("jobcardNumberDirection") || "Jobcard Direction";
    if (templateType === 4)
      return t("workorderNumberDirection") || "Workorder Direction";
    if (templateType === 6)
      return t("deliveryNoteNumberDirection") || "Delivery Note Direction";
    if (templateType === 5)
      return t("inspectionNumberDirection") || "Inspection Number Direction";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return (
        t("proformaInvoiceNumberDirection") ||
        "Proforma Invoice Number Direction"
      );
    }
    return t("invoiceNumberDirection") || "Invoice Number Direction";
  };

  const getFontSizeLabel = () => {
    if (templateType === 3)
      return t("jobcardNumberFontSize") || "Jobcard Number Font Size";
    if (templateType === 4)
      return t("workorderNumberFontSize") || "Workorder Number Font Size";
    if (templateType === 6)
      return (
        t("deliveryNoteNumberFontSize") || "Delivery Note Number Font Size"
      );
    if (templateType === 5)
      return t("inspectionNumberFontSize") || "Inspection Number Font Size";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return (
        t("proformaInvoiceNumberFontSize") ||
        "Proforma Invoice Number Font Size"
      );
    }
    return t("invoiceNumberFontSize") || "Invoice Number Font Size";
  };

  const getFontWeightLabel = () => {
    if (templateType === 3)
      return t("jobcardNumberFontWeight") || "Jobcard Number Font Weight";
    if (templateType === 4)
      return t("workorderNumberFontWeight") || "Workorder Number Font Weight";
    if (templateType === 6)
      return (
        t("deliveryNoteNumberFontWeight") || "Delivery Note Number Font Weight"
      );
    if (templateType === 5)
      return t("inspectionNumberFontWeight") || "Inspection Number Font Weight";
    if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      return (
        t("proformaInvoiceNumberFontWeight") ||
        "Proforma Invoice Number Font Weight"
      );
    }
    return t("invoiceNumberFontWeight") || "Invoice Number Font Weight";
  };

  const handleShowNumberChange = (checked: boolean) => {
    if (templateType === 3) {
      onChange({ showJobcardNumber: checked });
    } else if (templateType === 4) {
      onChange({ showWorkorderNumber: checked });
    } else if (templateType === 6) {
      onChange({ showDeliveryNoteNumber: checked });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ showProformaInvoiceNumber: checked });
    } else {
      onChange({ showInvoiceNumber: checked });
    }
  };

  const handleNumberPositionChange = (value: string) => {
    if (templateType === 3) {
      onChange({ jobcardNumberPosition: value as any });
    } else if (templateType === 4) {
      onChange({ workorderNumberPosition: value as any });
    } else if (templateType === 6) {
      onChange({ deliveryNoteNumberPosition: value as any });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ proformaInvoiceNumberPosition: value as any });
    } else {
      onChange({ invoiceNumberPosition: value as any });
    }
  };

  const handleNumberFontSizeChange = (value: number) => {
    if (templateType === 3) {
      onChange({ jobcardNumberFontSize: value });
    } else if (templateType === 4) {
      onChange({ workorderNumberFontSize: value });
    } else if (templateType === 6) {
      onChange({ deliveryNoteNumberFontSize: value });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ proformaInvoiceNumberFontSize: value });
    } else {
      onChange({ invoiceNumberFontSize: value });
    }
  };

  const handleNumberFontWeightChange = (value: any) => {
    if (templateType === 3) {
      onChange({ jobcardNumberFontWeight: value });
    } else if (templateType === 4) {
      onChange({ workorderNumberFontWeight: value });
    } else if (templateType === 6) {
      onChange({ deliveryNoteNumberFontWeight: value });
    } else if (templateType === BusinessTemplateType.PROFORMA_INVOICE) {
      onChange({ proformaInvoiceNumberFontWeight: value });
    } else {
      onChange({ invoiceNumberFontWeight: value });
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Show Logo */}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={settings.showLogo ?? true}
            onChange={(e) => onChange({ showLogo: e.target.checked })}
            disabled={disabled}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" } }}
          />
        }
        label={t("showLogo")}
        sx={{ m: 0 }}
      />

      {/* Logo Position */}
      {settings.showLogo && (
        <Box>
          <label
            className="font-Saira font-[600] text-[13px] mb-2 block"
            style={{ color: "#333" }}
          >
            {t("logoPosition")}
          </label>
          <RadioGroup
            row
            value={settings.logoPosition || "left"}
            onChange={(e) => onChange({ logoPosition: e.target.value as any })}
            sx={{ mt: 1 }}
          >
            {positionOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#4c0055" } }}
                    disabled={disabled}
                  />
                }
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Box>
      )}

      {/* Logo Size */}
      {settings.showLogo && settings.logoPosition !== "none" && (
        <Box>
          <label
            className="font-Saira font-[600] text-[13px] mb-2 block"
            style={{ color: "#333" }}
          >
            {t("logoSize")}
          </label>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={settings.useDefaultLogoSize ?? true}
                onChange={(e) =>
                  onChange({ useDefaultLogoSize: e.target.checked })
                }
                disabled={disabled}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" },
                }}
              />
            }
            label={t("useDefaultSize")}
            sx={{ m: 0, mb: 2 }}
          />
          {!settings.useDefaultLogoSize && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={settings.logoFitToWidth ?? false}
                    onChange={(e) =>
                      onChange({ logoFitToWidth: e.target.checked })
                    }
                    disabled={disabled}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#4c0055",
                      },
                    }}
                  />
                }
                label={t("logoFitToWidth")}
                sx={{ m: 0 }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                {!settings.logoFitToWidth && (
                  <TextField
                    type="number"
                    size="small"
                    label={t("logoWidth")}
                    value={settings.logoWidth || 150}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const value = Number.isFinite(raw) ? raw : 150;
                      onChange({
                        logoWidth: Math.min(
                          Math.max(value, 1),
                          PAGE_CONTENT_MAX_WIDTH_PX,
                        ),
                      });
                    }}
                    disabled={disabled}
                    helperText={t("logoMaxWidthHint", {
                      max: PAGE_CONTENT_MAX_WIDTH_PX,
                    })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">px</InputAdornment>
                      ),
                    }}
                    inputProps={{
                      max: PAGE_CONTENT_MAX_WIDTH_PX,
                      min: 1,
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiFormHelperText-root": {
                        margin: "40px 0 0 0",
                      },
                    }}
                  />
                )}
                <TextField
                  type="number"
                  size="small"
                  label={t("logoHeight")}
                  value={settings.logoHeight || 80}
                  onChange={(e) =>
                    onChange({
                      logoHeight: Math.max(
                        parseInt(e.target.value, 10) || 80,
                        1,
                      ),
                    })
                  }
                  disabled={disabled}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">px</InputAdornment>
                    ),
                  }}
                  inputProps={{ min: 1 }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Show Invoice/Jobcard/Workorder Number */}
      <FormControlLabel
        style={{ marginTop: "50px !important" }}
        control={
          <Switch
            size="small"
            checked={getShowNumberField() !== false}
            onChange={(e) => handleShowNumberChange(e.target.checked)}
            disabled={disabled}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" } }}
          />
        }
        label={getLabelText()}
        sx={{ m: 0 }}
      />

      {/* Invoice/Jobcard/Workorder Number Position */}
      {getShowNumberField() !== false && (
        <>
          <Box>
            <label
              className="font-Saira font-[600] text-[13px] mb-2 block"
              style={{ color: "#333" }}
            >
              {getPositionLabel()}
            </label>
            <RadioGroup
              row
              value={getNumberPositionField() || "center"}
              onChange={(e) => handleNumberPositionChange(e.target.value)}
              sx={{ mt: 1 }}
            >
              {positionOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio
                      size="small"
                      sx={{ "&.Mui-checked": { color: "#4c0055" } }}
                      disabled={disabled}
                    />
                  }
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </Box>

          {/* Invoice/Jobcard/Workorder Direction */}
          <Box>
            <label
              className="font-Saira font-[600] text-[13px] mb-2 block"
              style={{ color: "#333" }}
            >
              {getDirectionLabel()}
            </label>
            <RadioGroup
              row
              value={settings.showInvoiceDirection || "row"}
              onChange={(e) =>
                onChange({ showInvoiceDirection: e.target.value as any })
              }
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="row"
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#4c0055" } }}
                    disabled={disabled}
                  />
                }
                label={t("separate") || "Separate"}
              />
              <FormControlLabel
                value="column"
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#4c0055" } }}
                    disabled={disabled}
                  />
                }
                label={t("combineNumberAndLogo") || "Column"}
              />
            </RadioGroup>
          </Box>

          {/* Invoice/Jobcard/Workorder Number Font Size */}
          {getNumberPositionField() !== "none" && (
            <Box>
              <label
                className="font-Saira font-[600] text-[13px] mb-2 block"
                style={{ color: "#333" }}
              >
                {getFontSizeLabel()}
              </label>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={getNumberFontSizeField() || 18}
                onChange={(e) =>
                  handleNumberFontSizeChange(parseInt(e.target.value, 10) || 18)
                }
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">px</InputAdornment>
                  ),
                }}
                inputProps={{ min: 8, max: 72 }}
              />
            </Box>
          )}

          {/* Invoice/Jobcard/Workorder Number Font Weight */}
          {getNumberPositionField() !== "none" && (
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>{getFontWeightLabel()}</InputLabel>
                <Select
                  value={getNumberFontWeightField() || 700}
                  onChange={(e) => handleNumberFontWeightChange(e.target.value)}
                  disabled={disabled}
                  label={getFontWeightLabel()}
                >
                  {fontWeightOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </>
      )}

      {/* Show QR Code */}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={settings.showQR ?? false}
            onChange={(e) => onChange({ showQR: e.target.checked })}
            disabled={disabled}
            sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4c0055" } }}
          />
        }
        label={t("showQR")}
        sx={{ m: 0 }}
      />

      {/* QR Position (normal layouts only — thermal uses Layout “QR code” section order) */}
      {settings.showQR && !isThermalInvoice && (
        <Box>
          <label
            className="font-Saira font-[600] text-[13px] mb-2 block"
            style={{ color: "#333" }}
          >
            {t("qrPosition") || "QR Code Position"}
          </label>
          <RadioGroup
            row
            value={settings.qrPosition || "right"}
            onChange={(e) => onChange({ qrPosition: e.target.value as any })}
            sx={{ mt: 1 }}
          >
            {positionOptions.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={
                  <Radio
                    size="small"
                    sx={{ "&.Mui-checked": { color: "#4c0055" } }}
                    disabled={disabled}
                  />
                }
                label={option.label}
              />
            ))}
          </RadioGroup>
        </Box>
      )}
      {settings.showQR && isThermalInvoice && (
        <Typography variant="caption" sx={{ display: "block", color: "#666" }}>
          {t("thermalQrOrderingHint")}
        </Typography>
      )}
    </Box>
  );
};

export default LogoSettingsPanel;
